/* eslint-disable @typescript-eslint/no-explicit-any */
import classNames from 'classnames';
import {
  Accept,
  DropEvent,
  FileRejection,
  DropzoneProps as ReactDropZoneProps,
  useDropzone
} from 'react-dropzone';
import Button from './Button';
import imageIcon from 'assets/img/icons/image-icon.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
  ReactNode
} from 'react';
import AttachmentPreview, { FileAttachment } from 'components/common/AttachmentPreview';
import { convertFileToAttachment } from 'helpers/utils';
import ImageAttachmentPreview from 'components/common/ImageAttachmentPreview';
import { faTrashAlt, faFileLines } from '@fortawesome/free-solid-svg-icons';

interface DropzoneProps {
  className?: string;
  size?: 'sm';
  reactDropZoneProps?: ReactDropZoneProps;
  accept?: Accept;
  noPreview?: boolean;

  /**
   * Modo NÃO controlado (legado):
   * - Dropzone mantém estado interno.
   * - defaultFiles serve como valor inicial/sync.
   */
  defaultFiles?: File[];

  multiple?: boolean;

  /**
   * Legado (se já existir na base):
   * Recebe os arquivos atuais (controlado ou não)
   */
  setPhotos?: Dispatch<SetStateAction<File[]>>;

  /**
   * Callback do react-dropzone
   */
  onDrop?: <T extends File>(
    acceptedFiles: T[],
    fileRejections: FileRejection[],
    event: DropEvent
  ) => void;

  /**
   * Texto/UI (genérico)
   */
  dragText?: string;
  orText?: string;
  browseText?: string;
  helperText?: string;
  icon?: ReactNode;
  iconVariant?: 'image' | 'file' | 'auto';

  /**
   * Callback “alto nível” sempre que a lista muda
   */
  onFilesChange?: (files: File[]) => void;

  /**
   *  Modo CONTROLADO:
   * se você passar "value", o Dropzone renderiza a lista a partir desse valor
   * (ideal para Redux).
   */
  value?: File[];

  /**
   *  Comportamento ao soltar/selecionar:
   * - false: substitui a lista atual (comportamento antigo)
   * - true: adiciona aos arquivos já existentes
   */
  appendOnDrop?: boolean;
}

const Dropzone = ({
  className,
  size,
  onDrop,
  accept,
  defaultFiles = [],
  noPreview,
  reactDropZoneProps,
  multiple = true,
  setPhotos,
  children,
  dragText,
  orText = 'or',
  browseText = 'Browse from device',
  helperText,
  icon,
  iconVariant = 'image',
  onFilesChange,
  value,
  appendOnDrop = false
}: PropsWithChildren<DropzoneProps>) => {
  const isControlled = Array.isArray(value);

  // Estado interno só para modo NÃO-controlado
  const [internalFiles, setInternalFiles] = useState<File[]>(defaultFiles);

  // Fonte única de verdade para render
  const files = isControlled ? (value as File[]) : internalFiles;

  const imageOnly = useMemo(() => {
    return Boolean(accept && accept['image/*']);
  }, [accept]);

  // Sincroniza defaultFiles SEMPRE (inclusive limpando) no modo não-controlado
  useEffect(() => {
    if (!isControlled) {
      setInternalFiles(defaultFiles ?? []);
    }
  }, [defaultFiles, isControlled]);

  // Mantém setPhotos sincronizado, mesmo quando a lista fica vazia
  useEffect(() => {
    if (setPhotos) setPhotos(files);
  }, [files, setPhotos]);

  // Previews sempre derivados da lista atual
  const previews: FileAttachment[] = useMemo(() => {
    if (imageOnly) return [];
    return (files ?? []).map(file => convertFileToAttachment(file));
  }, [files, imageOnly]);

  const emitChange = (next: File[]) => {
    onFilesChange?.(next);
    setPhotos?.(next);
  };

  const setFilesSafe = (next: File[]) => {
    if (!isControlled) setInternalFiles(next);
    emitChange(next);
  };

  const handleRemoveFile = (index: number) => {
    const nextFiles = files.filter((_, ind) => ind !== index);
    setFilesSafe(nextFiles);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (...args) => {
      const [acceptedFiles, fileRejections, event] = args;

      const nextFiles = appendOnDrop ? [...files, ...acceptedFiles] : acceptedFiles;

      setFilesSafe(nextFiles);

      if (onDrop) onDrop(acceptedFiles as any, fileRejections, event);
    },
    multiple,
    accept,
    ...reactDropZoneProps
  });

  const defaultDragText = `Drag your ${imageOnly ? 'photo' : 'files'} here`;
  const finalDragText = dragText ?? defaultDragText;

  const renderDefaultIcon = () => {
    if (icon) return icon;

    const useFileIcon =
      iconVariant === 'file' || (iconVariant === 'auto' && !imageOnly);

    if (useFileIcon) {
      return (
        <FontAwesomeIcon
          icon={faFileLines}
          className={classNames('text-primary', {
            'fs-2': size !== 'sm',
            'fs-3': size === 'sm'
          })}
        />
      );
    }

    return (
      <img
        className="mt-3"
        src={imageIcon}
        width={size === 'sm' ? 24 : 40}
        alt=""
      />
    );
  };

  return (
    <>
      <div
        {...getRootProps()}
        className={classNames(className, 'dropzone', {
          'dropzone-sm': size === 'sm'
        })}
      >
        <input {...getInputProps()} />

        {children ? (
          <>{children}</>
        ) : (
          <div className="text-body-tertiary text-opacity-85 fw-bold fs-9">
            {finalDragText}{' '}
            <span className="text-body-secondary">{orText} </span>
            <Button variant="link" className="p-0" type="button">
              {browseText}
            </Button>

            <div className="mt-3 d-flex justify-content-center">
              {renderDefaultIcon()}
            </div>

            {helperText && (
              <div className="text-muted fw-normal mt-2">
                {helperText}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lista de anexos (não imagem) */}
      {!imageOnly &&
        previews.map((file, index) => (
          <div
            key={`${file.name}-${index}`}
            className={classNames(
              'border-bottom border-translucent d-flex align-items-center justify-content-between py-3'
            )}
          >
            <AttachmentPreview attachment={file} />
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleRemoveFile(index)}
            >
              <FontAwesomeIcon icon={faTrashAlt} className="fs-0" />
            </button>
          </div>
        ))}

      {/* Preview de imagens */}
      {imageOnly && !noPreview && files.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mt-3">
          {files.map((file, index) => (
            <ImageAttachmentPreview
              key={`${file.name}-${index}`}
              image={URL.createObjectURL(file)}
              handleClose={() => handleRemoveFile(index)}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default Dropzone;